import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ChartBarIcon, MapIcon, ClipboardDocumentCheckIcon, BoltIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import PanelSelectionModal from '../components/PanelSelectionModal';
import AppDownloadSection from '../components/AppDownloadSection';
import { executeRecaptcha, initRecaptcha } from '../utils/recaptcha';
import { buildDownloadUrl } from '../utils/downloadLinks';
import { getOrCreateMobileDeviceId } from '../utils/mobileDeviceId';
import { signDeviceAttestation } from '../utils/locationCrypto';
import brandLogo from '../assets/brand/logo-full.svg';
import '../styles/login-redesign.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1); // 1: email/password, 2: OTP verification
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPanelSelection, setShowPanelSelection] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPasswordSetupModal, setShowPasswordSetupModal] = useState(false);
  const [passwordSetupStep, setPasswordSetupStep] = useState(1); // 1: send OTP, 2: set password, 3: success
  const [passwordSetupData, setPasswordSetupData] = useState({
    email: '',
    otp: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordSetupErrors, setPasswordSetupErrors] = useState({});
  const [passwordSetupMessage, setPasswordSetupMessage] = useState('');
  const [passwordSetupLoading, setPasswordSetupLoading] = useState(false);
  const [passwordSetupOtpLoading, setPasswordSetupOtpLoading] = useState(false);
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  const [showSetupConfirmPassword, setShowSetupConfirmPassword] = useState(false);

  // Reset panel selection on component mount (e.g., after logout redirect)
  useEffect(() => {
    // Check if user is null or if localStorage is cleared (indicating logout)
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      setShowPanelSelection(false);
    }
    
    // Load saved credentials if remember me was checked
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    if (savedEmail && savedPassword) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail,
        password: savedPassword
      }));
      setRememberMe(true);
    }
    
    // Initialize reCAPTCHA on page load
    initRecaptcha();
  }, []);

  // Rotate feature highlights on each load
  const featureHighlights = useMemo(
    () => [
      {
        title: 'Smart Geofenced Attendance',
        subtitle: 'Auto-validate check-ins with location security and distance rules.'
      },
      {
        title: 'Manager Approvals, Simplified',
        subtitle: 'Approve check-outs, short leaves, and requests in one place.'
      },
      {
        title: 'Leave & WFH Tracking',
        subtitle: 'Streamlined requests, statuses, and policy-aware validations.'
      },
      {
        title: 'ClickUp Integration',
        subtitle: 'View tasks, workload timelines, and performance snapshots.'
      }
    ],
    []
  );
  const randomFeature = useMemo(
    () => featureHighlights[Math.floor(Math.random() * featureHighlights.length)],
    [featureHighlights]
  );
  const featureIcons = useMemo(() => [ChartBarIcon, MapIcon, ClipboardDocumentCheckIcon, BoltIcon], []);
  const RandomIcon = useMemo(() => featureIcons[Math.floor(Math.random() * featureIcons.length)], [featureIcons]);

  // Rotating quotes for login footer
  const quotes = useMemo(
    () => [
      { text: 'Productivity is never an accident; it is always the result of commitment.', author: 'Paul J. Meyer' },
      { text: 'What gets measured gets managed.', author: 'Peter Drucker' },
      { text: 'Small progress every day adds up to big results.', author: 'Unknown' },
      { text: 'Focus on being productive instead of busy.', author: 'Tim Ferriss' },
      { text: 'Action is the foundational key to all success.', author: 'Pablo Picasso' }
    ],
    []
  );
  const randomQuote = useMemo(() => quotes[Math.floor(Math.random() * quotes.length)], [quotes]);
  const smartLinkUrl = useMemo(
    () => buildDownloadUrl(window.location.href),
    []
  );

  const SpinnerSVG = () => (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      className="spinner-svg"
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 512 512"
    >
      <path d="M0 0 C52.8 0 105.6 0 160 0 C160 10.56 160 21.12 160 32 C157.36 32 154.72 32 152 32 C152 45.2 152 58.4 152 72 C181.04 72 210.08 72 240 72 C240 82.56 240 93.12 240 104 C237.36 104 234.72 104 232 104 C232 146.24 232 188.48 232 232 C205.6 232 179.2 232 152 232 C152 234.64 152 237.28 152 240 C104.48 240 56.96 240 8 240 C8 237.36 8 234.72 8 232 C-18.4 232 -44.8 232 -72 232 C-72 189.76 -72 147.52 -72 104 C-74.64 104 -77.28 104 -80 104 C-80 93.44 -80 82.88 -80 72 C-50.96 72 -21.92 72 8 72 C8 58.8 8 45.6 8 32 C5.36 32 2.72 32 0 32 C0 21.44 0 10.88 0 0 Z " fill="#596C80" transform="translate(176,264)"/>
      <path d="M0 0 C1.31201889 0.00081573 1.31201889 0.00081573 2.65054321 0.00164795 C40.62843455 0.10973252 93.37333268 10.06083268 121.6875 38.375 C121.94287109 40.29174805 121.94287109 40.29174805 121.9609375 42.63671875 C121.97842041 43.50087402 121.99590332 44.3650293 122.01391602 45.25537109 C122.02994873 46.18172363 122.04598145 47.10807617 122.0625 48.0625 C122.75191782 67.64196602 128.85350695 84.74110519 142.83984375 98.72265625 C156.26574116 111.22771021 171.65658417 117.26849429 189.6875 119.375 C202.71413251 148.60226685 208.6875 176.18059772 208.6875 208.375 C179.3175 208.375 149.9475 208.375 119.6875 208.375 C119.0275 200.455 118.3675 192.535 117.6875 184.375 C120.12125 183.35019531 122.555 182.32539063 125.0625 181.26953125 C127.44291671 180.26164176 129.82232911 179.25141611 132.20166016 178.2409668 C133.84353023 177.54516348 135.48641811 176.85175638 137.13037109 176.16088867 C139.50636354 175.16187545 141.87844424 174.15402419 144.25 173.14453125 C144.97429199 172.84223099 145.69858398 172.53993073 146.44482422 172.22846985 C154.18984658 168.91048756 160.71540286 165.30580207 165.6875 158.375 C167.64756086 152.49481742 167.51565527 145.77229157 165.5 139.9375 C161.28204347 133.97418215 156.85205442 130.30590432 149.63916016 128.69360352 C144.62277862 128.28915659 140.25261963 129.07748611 135.3359375 130.02734375 C133.87034897 130.29581871 133.87034897 130.29581871 132.37515259 130.56971741 C129.26891412 131.14030072 126.16568095 131.72553539 123.0625 132.3125 C119.95181373 132.89358813 116.84059628 133.47149382 113.72842407 134.04457092 C111.79645034 134.40107095 109.86538696 134.76254979 107.93539429 135.12962341 C106.61845078 135.37471397 106.61845078 135.37471397 105.27490234 135.62475586 C104.12409683 135.84245888 104.12409683 135.84245888 102.95004272 136.06455994 C96.47599827 136.95285378 96.47599827 136.95285378 94.02514648 135.15673828 C93.29920288 134.30958252 93.29920288 134.30958252 92.55859375 133.4453125 C92.00502197 132.81963379 91.4514502 132.19395508 90.88110352 131.54931641 C90.01183228 130.53490479 90.01183228 130.53490479 89.125 129.5 C67.57619607 105.48662044 39.18250523 91.91962472 7.0234375 89.5703125 C-16.05017263 88.4153487 -38.36203686 93.82667997 -58.3125 105.375 C-58.92174316 105.72433594 -59.53098633 106.07367188 -60.15869141 106.43359375 C-69.23016577 111.72105038 -76.89459789 117.96742923 -84.3125 125.375 C-85.17617187 126.22707031 -86.03984375 127.07914062 -86.9296875 127.95703125 C-108.51365797 150.38149902 -115.55176987 178.00696857 -118.3125 208.375 C-147.6825 208.375 -177.0525 208.375 -207.3125 208.375 C-207.3125 183.92244482 -207.3125 183.92244482 -205.6875 174.875 C-205.50928711 173.83512939 -205.33107422 172.79525879 -205.14746094 171.72387695 C-201.9089569 153.7436039 -196.50509485 135.7601897 -188.3125 119.375 C-185.72234857 118.85395791 -183.17439022 118.43538722 -180.5625 118.0625 C-162.52642862 115.15040628 -146.36648444 106.91671012 -135.1875 92.1875 C-127.48590603 81.10324515 -121.20623699 68.60634924 -121.234375 54.875 C-121.23091064 53.9061084 -121.22744629 52.9372168 -121.22387695 51.93896484 C-121.24092071 50.02146311 -121.30755477 48.10357866 -121.43041992 46.18994141 C-121.48500929 41.88706024 -121.26259672 39.38534731 -118.24511719 36.17651367 C-115.36515435 34.01081701 -112.43536144 32.17229603 -109.3125 30.375 C-108.31178467 29.78557617 -107.31106934 29.19615234 -106.2800293 28.58886719 C-83.69175715 15.77369282 -59.97840928 6.66447123 -34.3125 2.375 C-33.21172119 2.19098633 -32.11094238 2.00697266 -30.97680664 1.81738281 C-20.63436981 0.2594565 -10.44291351 -0.02454152 0 0 Z " fill="#F1AA49" transform="translate(255.3125,15.625)"/>
      <path d="M0 0 C3.18952583 -0.01946912 6.37903611 -0.03114791 9.56860352 -0.04150391 C10.91271175 -0.04985008 10.91271175 -0.04985008 12.28397369 -0.05836487 C19.28400182 -0.07538609 26.0298106 0.45443367 32.9375 1.625 C34.02885254 1.80321289 35.12020508 1.98142578 36.24462891 2.16503906 C63.47461672 6.8520706 100.78051319 17.78051319 121 38 C121.25537109 39.91674805 121.25537109 39.91674805 121.2734375 42.26171875 C121.29966187 43.55795166 121.29966187 43.55795166 121.32641602 44.88037109 C121.34244873 45.80672363 121.35848145 46.73307617 121.375 47.6875 C122.06441782 67.26696602 128.16600695 84.36610519 142.15234375 98.34765625 C155.57824116 110.85271021 170.96908417 116.89349429 189 119 C202.02663251 148.22726685 208 175.80559772 208 208 C178.63 208 149.26 208 119 208 C118.34 200.08 117.68 192.16 117 184 C120.650625 182.46279297 120.650625 182.46279297 124.375 180.89453125 C126.75541671 179.88664176 129.13482911 178.87641611 131.51416016 177.8659668 C133.15603023 177.17016348 134.79891811 176.47675638 136.44287109 175.78588867 C138.81886354 174.78687545 141.19094424 173.77902419 143.5625 172.76953125 C144.28679199 172.46723099 145.01108398 172.16493073 145.75732422 171.85346985 C153.50234658 168.53548756 160.02790286 164.93080207 165 158 C166.96006086 152.11981742 166.82815527 145.39729157 164.8125 139.5625 C160.59454347 133.59918215 156.16455442 129.93090432 148.95166016 128.31860352 C143.93527862 127.91415659 139.56511963 128.70248611 134.6484375 129.65234375 C133.18284897 129.92081871 133.18284897 129.92081871 131.68765259 130.19471741 C128.58141412 130.76530072 125.47818095 131.35053539 122.375 131.9375 C119.26431373 132.51858813 116.15309628 133.09649382 113.04092407 133.66957092 C111.10895034 134.02607095 109.17788696 134.38754979 107.24789429 134.75462341 C106.36993195 134.91801712 105.4919696 135.08141083 104.58740234 135.24975586 C103.43659683 135.46745888 103.43659683 135.46745888 102.26254272 135.68955994 C95.78849827 136.57785378 95.78849827 136.57785378 93.33764648 134.78173828 C92.85368408 134.21696777 92.36972168 133.65219727 91.87109375 133.0703125 C91.04073608 132.13179443 91.04073608 132.13179443 90.19360352 131.17431641 C89.32433228 130.15990479 89.32433228 130.15990479 88.4375 129.125 C66.02928828 104.15392063 37.96916478 92.06494529 4.91015625 89.39453125 C1 89 1 89 0 88 C-0.09516211 85.2611158 -0.12554594 82.54752059 -0.11352539 79.80834961 C-0.11367142 78.94988937 -0.11381744 78.09142914 -0.1139679 77.20695496 C-0.11326847 74.35539262 -0.1054747 71.50389475 -0.09765625 68.65234375 C-0.09579218 66.68079158 -0.09436822 64.70923895 -0.09336853 62.73768616 C-0.08954251 57.53773733 -0.07971152 52.33781625 -0.06866455 47.13787842 C-0.05845222 41.83606339 -0.05387129 36.5342439 -0.04882812 31.23242188 C-0.03808933 20.82160237 -0.02101191 10.41080349 0 0 Z " fill="#72BD43" transform="translate(256,16)"/>
      <path d="M0 0 C26.4 0 52.8 0 80 0 C80 42.24 80 84.48 80 128 C53.6 128 27.2 128 0 128 C0 85.76 0 43.52 0 0 Z " fill="#465666" transform="translate(328,368)"/>
      <path d="M0 0 C26.4 0 52.8 0 80 0 C80 42.24 80 84.48 80 128 C53.6 128 27.2 128 0 128 C0 85.76 0 43.52 0 0 Z " fill="#465666" transform="translate(104,368)"/>
      <path d="M0 0 C2.44225447 1.66159248 4.74768159 3.38773224 7.0390625 5.25 C7.86148438 5.86875 8.68390625 6.4875 9.53125 7.125 C19.01135737 15.15798818 26.15442074 27.99599421 27.23193359 40.4855957 C27.80176157 57.74447646 26.33180617 71.35512524 15.0390625 85.25 C14.41644531 86.08144531 13.79382813 86.91289063 13.15234375 87.76953125 C5.06558632 97.26138146 -8.2378086 104.14149419 -20.63989258 105.55541992 C-23.37919435 105.68602872 -26.09374992 105.71781637 -28.8359375 105.6875 C-29.80120361 105.67992676 -30.76646973 105.67235352 -31.76098633 105.66455078 C-39.0908864 105.49542298 -45.31274169 104.48425742 -51.9609375 101.25 C-52.92257812 100.79109375 -53.88421875 100.3321875 -54.875 99.859375 C-68.84357128 92.70565925 -77.83604015 81.93720914 -83.0859375 67.0625 C-87.46207351 52.99634854 -86.39562354 36.47327982 -79.9609375 23.25 C-77.85984497 19.69502648 -75.56306644 16.45172464 -72.9609375 13.25 C-72.3421875 12.42757813 -71.7234375 11.60515625 -71.0859375 10.7578125 C-54.25015514 -9.11088653 -22.01325939 -13.01047521 0 0 Z " fill="#3E9944" transform="translate(476.9609375,14.75)"/>
      <path d="M0 0 C2.44225447 1.66159248 4.74768159 3.38773224 7.0390625 5.25 C7.86148438 5.86875 8.68390625 6.4875 9.53125 7.125 C19.01135737 15.15798818 26.15442074 27.99599421 27.23193359 40.4855957 C27.80176157 57.74447646 26.33180617 71.35512524 15.0390625 85.25 C14.41644531 86.08144531 13.79382813 86.91289063 13.15234375 87.76953125 C5.06558632 97.26138146 -8.2378086 104.14149419 -20.63989258 105.55541992 C-23.37919435 105.68602872 -26.09374992 105.71781637 -28.8359375 105.6875 C-29.80120361 105.67992676 -30.76646973 105.67235352 -31.76098633 105.66455078 C-39.0908864 105.49542298 -45.31274169 104.48425742 -51.9609375 101.25 C-52.92257813 100.79109375 -53.88421875 100.3321875 -54.875 99.859375 C-68.84357128 92.70565925 -77.83604015 81.93720914 -83.0859375 67.0625 C-87.46207351 52.99634854 -86.39562354 36.47327982 -79.9609375 23.25 C-77.85984497 19.69502648 -75.56306644 16.45172464 -72.9609375 13.25 C-72.3421875 12.42757813 -71.7234375 11.60515625 -71.0859375 10.7578125 C-54.25015514 -9.11088653 -22.01325939 -13.01047521 0 0 Z " fill="#D9575D" transform="translate(92.9609375,14.75)"/>
      <path d="M0 0 C4.44130243 0.42541465 6.89770019 3.48258254 9.859375 6.49609375 C10.683237 7.31920799 10.683237 7.31920799 11.52374268 8.15895081 C12.68040774 9.31795259 13.83339017 10.48063974 14.98291016 11.64672852 C16.74873953 13.43622264 18.52758608 15.21195318 20.30859375 16.98632812 C21.43048327 18.11379206 22.55160421 19.2420214 23.671875 20.37109375 C24.20617126 20.90447861 24.74046753 21.43786346 25.29095459 21.9874115 C29 25.76896324 29 25.76896324 29 28 C30.485 28.495 30.485 28.495 32 29 C32 29.66 32 30.32 32 31 C33.32 31.66 34.64 32.32 36 33 C36 33.66 36 34.32 36 35 C37.32 35.66 38.64 36.32 40 37 C40 37.66 40 38.32 40 39 C41.485 39.495 41.485 39.495 43 40 C43.95703125 41.765625 43.95703125 41.765625 44 44 C42.26171875 46.234375 42.26171875 46.234375 39.6875 48.75 C18.97018598 70.1739625 12.62137373 98.16488893 10 127 C-19.37 127 -48.74 127 -79 127 C-79 102.54744482 -79 102.54744482 -77.375 93.5 C-77.19678711 92.46012939 -77.01857422 91.42025879 -76.83496094 90.34887695 C-73.5964569 72.3686039 -68.19259485 54.3851897 -60 38 C-57.40984857 37.47895791 -54.86189022 37.06038722 -52.25 36.6875 C-34.02320957 33.74461289 -17.77483802 25.41346211 -6.6875 10.375 C-4.30221447 6.99786146 -2.09199805 3.56605206 0 0 Z " fill="#D9575C" transform="translate(127,97)"/>
      <path d="M0 0 C1.62244666 2.15110264 3.2158871 4.30827338 4.76171875 6.515625 C16.75020781 23.53782042 32.38610873 34.05322651 53 38 C55.66367443 38.37142145 58.32808556 38.69004645 61 39 C74.02663251 68.22726685 80 95.80559772 80 128 C50.63 128 21.26 128 -9 128 C-9.66 120.08 -10.32 112.16 -11 104 C-8.56625 102.97519531 -6.1325 101.95039063 -3.625 100.89453125 C-1.24458329 99.88664176 1.13482911 98.87641611 3.51416016 97.8659668 C5.15603023 97.17016348 6.79891811 96.47675638 8.44287109 95.78588867 C10.81886354 94.78687545 13.19094424 93.77902419 15.5625 92.76953125 C16.28679199 92.46723099 17.01108398 92.16493073 17.75732422 91.85346985 C25.50234658 88.53548756 32.02790286 84.93080207 37 78 C38.96006086 72.11981742 38.82815527 65.39729157 36.8125 59.5625 C32.59454347 53.59918215 28.16455442 49.93090432 20.95166016 48.31860352 C15.93527862 47.91415659 11.56511963 48.70248611 6.6484375 49.65234375 C5.67137848 49.83132706 4.69431946 50.01031036 3.68765259 50.19471741 C0.58141412 50.76530072 -2.52181905 51.35053539 -5.625 51.9375 C-8.73568627 52.51858813 -11.84690372 53.09649382 -14.95907593 53.66957092 C-16.89104966 54.02607095 -18.82211304 54.38754979 -20.75210571 54.75462341 C-21.63006805 54.91801712 -22.5080304 55.08141083 -23.41259766 55.24975586 C-24.17980133 55.3948912 -24.947005 55.54002655 -25.73745728 55.68955994 C-28.23285409 56.03194956 -30.48572697 56.20836508 -33 56 C-35.00267395 54.19905525 -36.61352605 52.54883673 -38.3125 50.5 C-38.76689453 49.97535156 -39.22128906 49.45070312 -39.68945312 48.91015625 C-40.8057012 47.61765847 -41.90460622 46.31021858 -43 45 C-41.52877384 41.59646978 -39.63057487 39.38744251 -37.00927734 36.78222656 C-35.77822769 35.5514035 -35.77822769 35.5514035 -34.52230835 34.29571533 C-33.63580597 33.41953552 -32.74930359 32.54335571 -31.8359375 31.640625 C-30.92872955 30.73624695 -30.02152161 29.8318689 -29.08682251 28.90008545 C-26.68115581 26.50203542 -24.26954264 24.11005382 -21.85662842 21.71929932 C-19.39297656 19.27614829 -16.93501423 16.82728794 -14.4765625 14.37890625 C-9.65626427 9.58066212 -4.83030157 4.78817268 0 0 Z " fill="#409844" transform="translate(384,96)"/>
      <path d="M0 0 C1.86618552 1.86618552 1.3485526 4.49777654 1.5234375 6.9921875 C1 9 1 9 -1.09960938 10.43237305 C-4.30734645 12.16611713 -7.58003731 13.65873621 -10.9296875 15.08984375 C-11.61522202 15.38600586 -12.30075653 15.68216797 -13.00706482 15.98730469 C-15.29482854 16.97434078 -17.58484967 17.95601541 -19.875 18.9375 C-21.51464838 19.64312162 -23.15421337 20.34893701 -24.79370117 21.05493164 C-42.90972659 28.84765953 -61.06041613 36.55978486 -79.21273804 44.26748657 C-85.90520573 47.10932653 -92.59570855 49.95574685 -99.28515625 52.8046875 C-101.96540186 53.94566567 -104.64569696 55.08652748 -107.32601929 56.22732544 C-108.98811561 56.93492775 -110.65008855 57.64281994 -112.31192017 58.3510437 C-116.80470534 60.26549013 -121.30129614 62.17050375 -125.80166626 64.06704712 C-128.08528878 65.03619579 -130.36136332 66.02184543 -132.63745117 67.00854492 C-134.1448609 67.65190175 -135.65242381 68.29489982 -137.16015625 68.9375 C-137.83791107 69.236064 -138.51566589 69.53462799 -139.21395874 69.84223938 C-146.49658253 72.91548831 -153.72019549 73.45766673 -161.43359375 71.328125 C-167.19648014 68.84431007 -171.37502619 64.27220753 -174.140625 58.703125 C-176.20986775 52.19441599 -176.27246784 43.79046513 -173.4375 37.5 C-168.67472089 29.75011204 -162.52817154 27.13811564 -154 25 C-149.1427663 23.95039471 -144.26185594 23.04272364 -139.37402344 22.14916992 C-137.92409303 21.87991151 -136.47424582 21.61020479 -135.0244751 21.34008789 C-131.11672274 20.61372213 -127.20765818 19.89468754 -123.29814434 19.17786956 C-120.85253158 18.7292133 -118.40718659 18.27912509 -115.96192169 17.82857704 C-107.42333417 16.25557591 -98.88356181 14.68917877 -90.34277344 13.12817383 C-82.39119582 11.67465201 -74.4425951 10.20557679 -66.49516654 8.72955436 C-59.65694388 7.46032656 -52.81666676 6.2026151 -45.97470498 4.95370179 C-41.89434975 4.20864445 -37.81500872 3.45883321 -33.7377243 2.69713402 C-29.90851794 1.98213518 -26.07695946 1.28148601 -22.24312592 0.59174347 C-20.83852468 0.33652344 -19.43484034 0.0761956 -18.0322113 -0.18964958 C-16.11500935 -0.5522016 -14.19399739 -0.89442911 -12.27288818 -1.235672 C-11.20000423 -1.43272641 -10.12712029 -1.62978083 -9.0217247 -1.83280659 C-5.53940479 -2.02548494 -3.20185749 -1.33121206 0 0 Z " fill="#286FCC" transform="translate(407,160)"/>
      <path d="M0 0 C52.8 0 105.6 0 160 0 C160 10.56 160 21.12 160 32 C107.2 32 54.4 32 0 32 C0 21.44 0 10.88 0 0 Z " fill="#8E9FB3" transform="translate(176,264)"/>
      <path d="M0 0 C15.84 0 31.68 0 48 0 C48 21.12 48 42.24 48 64 C32.16 64 16.32 64 0 64 C0 42.88 0 21.76 0 0 Z " fill="#F2F2F2" transform="translate(232,440)"/>
      <path d="M0 0 C29.04 0 58.08 0 88 0 C88 10.56 88 21.12 88 32 C58.96 32 29.92 32 0 32 C0 21.44 0 10.88 0 0 Z " fill="#8E9FB3" transform="translate(328,336)"/>
      <path d="M0 0 C29.04 0 58.08 0 88 0 C88 10.56 88 21.12 88 32 C58.96 32 29.92 32 0 32 C0 21.44 0 10.88 0 0 Z " fill="#8E9FB3" transform="translate(96,336)"/>
      <path d="M0 0 C5.28 0 10.56 0 16 0 C16.37125 1.381875 16.7425 2.76375 17.125 4.1875 C18.75004042 9.12482376 20.74489133 11.10290473 25 14 C28.45844824 15.72922412 32.21165265 15.35136443 36 15 C40.70813298 13.31217874 43.31740144 11.21551203 46 7 C46.66 4.69 47.32 2.38 48 0 C53.28 0 58.56 0 64 0 C63.71200042 8.6399874 60.97082245 16.25207149 55.36328125 22.95703125 C47.67260423 30.03819075 38.05187437 32.40071056 27.83984375 32.27734375 C20.37797314 31.54860535 13.27230515 27.21680721 8 22 C2.83875996 14.9733894 0 8.79282065 0 0 Z " fill="#EFF0EF" transform="translate(416,68)"/>
      <path d="M0 0 C6.45546141 4.54528923 11.01297115 9.58399552 12.45703125 17.5078125 C12.71056964 19.66457907 12.86453821 21.83261129 13 24 C7.72 24 2.44 24 -3 24 C-3.99 21.69 -4.98 19.38 -6 17 C-8.65739432 13.6782571 -11.08782884 13.10326466 -15.3125 12.625 C-20.06996825 13.10881033 -21.76367705 14.53788708 -25 18 C-25.66 19.98 -26.32 21.96 -27 24 C-32.28 24 -37.56 24 -43 24 C-42.42880863 14.86093808 -40.50234137 9.0672984 -33.75 2.6875 C-23.86838266 -5.49094427 -11.14403265 -7.03505972 0 0 Z " fill="#F1EFEF" transform="translate(79,68)"/>
      <path d="M0 0 C7.92 0 15.84 0 24 0 C24 7.92 24 15.84 24 24 C16.08 24 8.16 24 0 24 C0 16.08 0 8.16 0 0 Z " fill="#F2F2F2" transform="translate(272,384)"/>
      <path d="M0 0 C7.92 0 15.84 0 24 0 C24 7.92 24 15.84 24 24 C16.08 24 8.16 24 0 24 C0 16.08 0 8.16 0 0 Z " fill="#F2F2F2" transform="translate(216,384)"/>
      <path d="M0 0 C7.92 0 15.84 0 24 0 C24 7.92 24 15.84 24 24 C16.08 24 8.16 24 0 24 C0 16.08 0 8.16 0 0 Z " fill="#F2F2F2" transform="translate(272,328)"/>
      <path d="M0 0 C7.92 0 15.84 0 24 0 C24 7.92 24 15.84 24 24 C16.08 24 8.16 24 0 24 C0 16.08 0 8.16 0 0 Z " fill="#F2F2F2" transform="translate(216,328)"/>
      <path d="M0 0 C5.28 0 10.56 0 16 0 C16 5.28 16 10.56 16 16 C10.72 16 5.44 16 0 16 C0 10.72 0 5.44 0 0 Z " fill="#F2F2F2" transform="translate(456,40)"/>
      <path d="M0 0 C5.28 0 10.56 0 16 0 C16 5.28 16 10.56 16 16 C10.72 16 5.44 16 0 16 C0 10.72 0 5.44 0 0 Z " fill="#F2F2F2" transform="translate(424,40)"/>
      <path d="M0 0 C5.28 0 10.56 0 16 0 C16 5.28 16 10.56 16 16 C10.72 16 5.44 16 0 16 C0 10.72 0 5.44 0 0 Z " fill="#F2F2F2" transform="translate(72,40)"/>
      <path d="M0 0 C5.28 0 10.56 0 16 0 C16 5.28 16 10.56 16 16 C10.72 16 5.44 16 0 16 C0 10.72 0 5.44 0 0 Z " fill="#F2F2F2" transform="translate(40,40)"/>
    </svg>
  );

  // Reset panel selection when user logs out or when auth loading completes with no user
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        setShowPanelSelection(false);
      }
    }
  }, [user, authLoading]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      // Super admin (admin@mastersunion.org) always goes directly to admin dashboard - no panel selection
      // Use window.location to force immediate redirect and prevent any other routing
      if (user.isSuperAdmin && user.email === 'admin@mastersunion.org') {
        window.location.href = '/';
        return;
      }
      
      if (user.role === 'admin') {
        navigate('/', { replace: true });
      } else if (user.role === 'manager-admin') {
        navigate('/', { replace: true });
      } else if (user.role === 'user') {
        // Super admin should never be treated as a regular user
        if (user.isSuperAdmin && user.email === 'admin@mastersunion.org') {
          window.location.href = '/';
          return;
        }
        
        // Check user privileges for panel selection
        const isTeamLead = user.isTeamLead || user.hasSubordinates;
        const isManager = user.isManager;
        
        // Double-check that localStorage still has user data (not cleared by logout)
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (!token || !userData) {
          // Logout happened, don't show panel selection
          setShowPanelSelection(false);
          return;
        }
        
        if (isManager || isTeamLead) {
          // Don't show panel selection if we're already on user-dashboard
          if (location.pathname === '/user-dashboard') {
            // User chose Employee Account, stay on user-dashboard
            return;
          }
          setShowPanelSelection(true);
        } else {
          // Regular user with no management privileges
          navigate('/user-dashboard', { replace: true });
        }
      }
    }
  }, [user, authLoading, navigate, location.pathname]);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: ''
  });
  
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');

  const getDeviceSecurityPayload = async (email) => {
    const deviceId = await getOrCreateMobileDeviceId();
    if (!deviceId) {
      return {};
    }

    // Signed proof the enrollment came from the app, not a hand-rolled request.
    const deviceAttestation = await signDeviceAttestation({ deviceId, email });

    return deviceAttestation ? { deviceId, deviceAttestation } : { deviceId };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const openPasswordSetupModal = (email) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    setPasswordSetupData({
      email: normalizedEmail,
      otp: '',
      password: '',
      confirmPassword: ''
    });
    setPasswordSetupErrors({});
    setPasswordSetupMessage('');
    setPasswordSetupStep(1);
    setShowSetupPassword(false);
    setShowSetupConfirmPassword(false);
    setShowPasswordSetupModal(true);
  };

  const closePasswordSetupModal = () => {
    if (passwordSetupLoading || passwordSetupOtpLoading) {
      return;
    }
    setShowPasswordSetupModal(false);
    setPasswordSetupErrors({});
    setPasswordSetupMessage('');
    setPasswordSetupStep(1);
  };

  const handlePasswordSetupInputChange = (field, value) => {
    setPasswordSetupData(prev => ({
      ...prev,
      [field]: value
    }));

    if (passwordSetupErrors[field]) {
      setPasswordSetupErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordSetupSendOtp = async (e) => {
    e.preventDefault();

    if (passwordSetupOtpLoading || passwordSetupLoading) {
      return;
    }

    const trimmedEmail = passwordSetupData.email.trim().toLowerCase();
    if (!trimmedEmail) {
      setPasswordSetupErrors({ email: 'Email is required' });
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setPasswordSetupErrors({ email: 'Please enter a valid email address' });
      return;
    }

    try {
      setPasswordSetupOtpLoading(true);
      setPasswordSetupErrors({});

      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('otp_send');
      } catch (recaptchaError) {
        setPasswordSetupErrors({ email: 'reCAPTCHA verification failed. Please try again.' });
        setPasswordSetupOtpLoading(false);
        return;
      }

      const response = await axios.post('/api/auth/send-otp', {
        email: trimmedEmail,
        recaptchaToken
      });

      setPasswordSetupMessage(response.data.message || 'OTP sent successfully.');
      setPasswordSetupStep(2);
    } catch (error) {
      setPasswordSetupErrors({
        email: error.response?.data?.error || 'Failed to send OTP. Please try again.'
      });
    } finally {
      setPasswordSetupOtpLoading(false);
    }
  };

  const handlePasswordSetupSubmit = async (e) => {
    e.preventDefault();

    if (passwordSetupLoading) {
      return;
    }

    const trimmedEmail = passwordSetupData.email.trim().toLowerCase();
    const trimmedOtp = passwordSetupData.otp.trim();
    const newPassword = passwordSetupData.password;
    const confirmPassword = passwordSetupData.confirmPassword;

    if (!trimmedEmail) {
      setPasswordSetupErrors({ email: 'Email is required' });
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setPasswordSetupErrors({ email: 'Please enter a valid email address' });
      return;
    }

    if (!trimmedOtp) {
      setPasswordSetupErrors({ otp: 'OTP is required' });
      return;
    }

    if (trimmedOtp.length !== 6) {
      setPasswordSetupErrors({ otp: 'OTP must be 6 digits' });
      return;
    }

    if (!newPassword.trim()) {
      setPasswordSetupErrors({ password: 'Password is required' });
      return;
    }

    if (newPassword.length < 8) {
      setPasswordSetupErrors({ password: 'Password must be at least 8 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordSetupErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    try {
      setPasswordSetupLoading(true);
      setPasswordSetupErrors({});

      const response = await axios.post('/api/auth/set-password', {
        email: trimmedEmail,
        otp: trimmedOtp,
        password: newPassword
      });

      setPasswordSetupMessage(response.data.message || 'Password set successfully.');
      setPasswordSetupStep(3);

      setFormData(prev => ({
        ...prev,
        email: trimmedEmail,
        password: newPassword
      }));
      setLoginMethod('password');
      setStep(1);
      setErrors({});
    } catch (error) {
      setPasswordSetupErrors({
        otp: error.response?.data?.error || 'Failed to set password. Please try again.'
      });
    } finally {
      setPasswordSetupLoading(false);
    }
  };

  const shouldShowPasswordSetup = (message) => {
    return String(message || '').toLowerCase().includes('no password set');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      
      // Execute reCAPTCHA
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('otp_send');
      } catch (recaptchaError) {
        setErrors({ 
          email: 'reCAPTCHA verification failed. Please refresh the page and try again.' 
        });
        setLoading(false);
        return;
      }
      
      const response = await axios.post('/api/auth/send-otp', {
        email: formData.email.trim().toLowerCase(),
        recaptchaToken
      });
      
      setMessage(response.data.message);
      setStep(2);
    } catch (error) {
      setErrors({ 
        email: error.response?.data?.error || 'Failed to send OTP. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!formData.password.trim()) {
      setErrors({ password: 'Password is required' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      
      // Execute reCAPTCHA - generate fresh token right before submission
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('login', 1); // Allow 1 retry
      } catch (recaptchaError) {
        setErrors({ 
          password: 'reCAPTCHA verification failed. Please try again.' 
        });
        setLoading(false);
        return;
      }
      
      // Try admin login first with fresh token
      let response;
      try {
        const devicePayload = await getDeviceSecurityPayload(formData.email.trim().toLowerCase());
        response = await axios.post('/api/auth/admin-login', {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          recaptchaToken,
          ...devicePayload
        }, {
          withCredentials: true // Enable cookies for persistent sessions
        });
      } catch (adminError) {
        // If admin login fails, generate a NEW token for user login
        // Each API call needs its own fresh token
        let userRecaptchaToken;
        try {
          userRecaptchaToken = await executeRecaptcha('login');
        } catch (recaptchaError) {
          setErrors({ 
            password: adminError.response?.data?.error || 'Login failed. Please try again.' 
          });
          setLoading(false);
          return;
        }
        
        // Try manager/user login with password using the new token
        const devicePayload = await getDeviceSecurityPayload(formData.email.trim().toLowerCase());
        response = await axios.post('/api/auth/login-with-password', {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          recaptchaToken: userRecaptchaToken,
          ...devicePayload
        }, {
          withCredentials: true // Enable cookies for persistent sessions
        });
      }
      
      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email.trim().toLowerCase());
        localStorage.setItem('rememberedPassword', formData.password);
      } else {
        // Clear saved credentials if remember me is unchecked
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }
      
      // Super admin (admin@mastersunion.org) always goes directly to admin dashboard - no panel selection
      // Check BEFORE calling login and use window.location for immediate redirect
      if (response.data.user.isSuperAdmin && response.data.user.email === 'admin@mastersunion.org') {
        login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
        // Use window.location for immediate redirect, bypassing React Router
        window.location.href = '/';
        return;
      }
      
      // Use the login function from AuthContext
      login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
      
      // Defer navigation for regular users so the panel selection modal can render via useEffect
      // Admin/manager-admin can navigate to the admin dashboard immediately
      if (response.data.user.role !== 'user') {
        navigate('/', { replace: true });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Invalid email or password.';

      if (shouldShowPasswordSetup(errorMessage)) {
        openPasswordSetupModal(formData.email);
        setErrors({ password: errorMessage });
        return;
      }
      
      // If reCAPTCHA error, try once more with fresh token (only for actual reCAPTCHA errors)
      if (errorMessage.includes('reCAPTCHA') && (errorMessage.includes('expired') || errorMessage.includes('already used'))) {
        try {
          // Generate completely fresh token for retry
          const freshToken = await executeRecaptcha('login');
          
          // Retry admin login first with fresh token
          try {
            const devicePayload = await getDeviceSecurityPayload(formData.email.trim().toLowerCase());
            const response = await axios.post('/api/auth/admin-login', {
              email: formData.email.trim().toLowerCase(),
              password: formData.password,
              recaptchaToken: freshToken,
              ...devicePayload
            }, {
              withCredentials: true
            });
            
            // Save credentials if remember me is checked
            if (rememberMe) {
              localStorage.setItem('rememberedEmail', formData.email.trim().toLowerCase());
              localStorage.setItem('rememberedPassword', formData.password);
            } else {
              localStorage.removeItem('rememberedEmail');
              localStorage.removeItem('rememberedPassword');
            }
            
            if (response.data.user.isSuperAdmin && response.data.user.email === 'admin@mastersunion.org') {
              login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
              window.location.href = '/';
              return;
            }
            
            login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
            if (response.data.user.role !== 'user') {
              navigate('/', { replace: true });
            }
            return;
          } catch (adminRetryError) {
            // If admin login fails, generate another fresh token for user login
            const userFreshToken = await executeRecaptcha('login');
            const devicePayload = await getDeviceSecurityPayload(formData.email.trim().toLowerCase());
            const response = await axios.post('/api/auth/login-with-password', {
              email: formData.email.trim().toLowerCase(),
              password: formData.password,
              recaptchaToken: userFreshToken,
              ...devicePayload
            }, {
              withCredentials: true
            });
            
            // Save credentials if remember me is checked
            if (rememberMe) {
              localStorage.setItem('rememberedEmail', formData.email.trim().toLowerCase());
              localStorage.setItem('rememberedPassword', formData.password);
            } else {
              localStorage.removeItem('rememberedEmail');
              localStorage.removeItem('rememberedPassword');
            }
            
            login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
            if (response.data.user.role !== 'user') {
              navigate('/', { replace: true });
            }
            return;
          }
        } catch (retryError) {
          // If retry also fails, show error
          const retryMessage = retryError.response?.data?.error || 'Login failed. Please try again.';
          if (shouldShowPasswordSetup(retryMessage)) {
            openPasswordSetupModal(formData.email);
            setErrors({ password: retryMessage });
            return;
          }
          setErrors({ 
            password: retryMessage 
          });
        }
      } else {
        setErrors({ 
          password: errorMessage
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    if (!formData.otp.trim()) {
      setErrors({ otp: 'OTP is required' });
      return;
    }
    
    if (formData.otp.length !== 6) {
      setErrors({ otp: 'OTP must be 6 digits' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      
      // Execute reCAPTCHA
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('otp_verify');
      } catch (recaptchaError) {
        setErrors({ 
          otp: 'reCAPTCHA verification failed. Please refresh the page and try again.' 
        });
        setLoading(false);
        return;
      }
      
      const devicePayload = await getDeviceSecurityPayload(formData.email.trim().toLowerCase());
      const response = await axios.post('/api/auth/verify-otp', {
        email: formData.email.trim().toLowerCase(),
        otp: formData.otp.trim(),
        recaptchaToken,
        ...devicePayload
      }, {
        withCredentials: true // Enable cookies for persistent sessions
      });
      
      // Super admin (admin@mastersunion.org) always goes directly to admin dashboard - no panel selection
      // Check BEFORE calling login and use window.location for immediate redirect
      if (response.data.user.isSuperAdmin && response.data.user.email === 'admin@mastersunion.org') {
        login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
        // Use window.location for immediate redirect, bypassing React Router
        window.location.href = '/';
        return;
      }
      
      // Use the login function from AuthContext
      login(response.data.token, response.data.user, { deviceLock: response.data.deviceLock, deviceRegistration: response.data.deviceRegistration });
      
      // Defer navigation for regular users so the panel selection modal can render via useEffect
      // Admin/manager-admin can navigate to the admin dashboard immediately
      if (response.data.user.role !== 'user') {
        navigate('/', { replace: true });
      }
    } catch (error) {
      setErrors({ 
        otp: error.response?.data?.error || 'Invalid or expired OTP. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };


  const handleBackToEmail = () => {
    setStep(1);
    setLoginMethod('password');
    setFormData(prev => ({ ...prev, otp: '' }));
    setErrors({});
    setMessage('');
  };

  const switchToOTP = () => {
    setLoginMethod('otp');
    setFormData(prev => ({ ...prev, password: '' }));
    setErrors({});
  };

  const switchToPassword = () => {
    setLoginMethod('password');
    setFormData(prev => ({ ...prev, otp: '' }));
    setErrors({});
    setMessage('');
  };

  // Show loading while auth context is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col justify-center items-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left Panel - Feature Highlight */}
      <div className="login-left-panel">
        <div className="greeting-content">
          <div className="feature-icon-wrap">
            <RandomIcon className="h-10 w-10 text-white" />
          </div>
          <h2>Discover</h2>
          <h1 className="feature-title">{randomFeature.title}</h1>
          <p className="feature-subtitle">{randomFeature.subtitle}</p>
          <AppDownloadSection smartLinkUrl={smartLinkUrl} variant="hero" />
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="login-right-panel">
        <div className="login-right-panel-content">
          <div className="login-form-container">
            <div className="login-header">
              <img src={brandLogo} alt="Coach EMS" className="login-brand-logo" />
              <h1 className="login-title">Login</h1>
              <p className="login-subtitle">Sign in to your Employee Management System</p>
            </div>

            {step === 1 && (
              <div className="step-transition">
                <form onSubmit={loginMethod === 'otp' ? handleSendOTP : handlePasswordLogin} className="login-form">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Username</label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`form-input ${errors.email ? 'border-red-300' : ''}`}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="error-message">{errors.email}</p>
                    )}
                  </div>

                  {loginMethod === 'password' && (
                    <div className="form-group">
                      <label htmlFor="password" className="form-label">Password</label>
                      <div className="password-input-container">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className={`form-input ${errors.password ? 'border-red-300' : ''}`}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="password-toggle"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="error-message">{errors.password}</p>
                      )}
                    </div>
                  )}

                  {loginMethod === 'password' && (
                    <div className="form-group">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                        />
                        <span className="ml-2 text-sm text-gray-700">Remember me</span>
                      </label>
                    </div>
                  )}

                  <div className="login-links">
                    <button
                      type="button"
                      onClick={loginMethod === 'password' ? switchToOTP : switchToPassword}
                      className="login-link"
                    >
                      {loginMethod === 'password' ? 'Login with OTP' : 'Login with Password'}
                    </button>
                    <Link to="/forgot-password" className="login-link">
                      forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="login-button"
                  >
                    {loading ? (
                      <>
                        <SpinnerSVG />
                        {loginMethod === 'otp' ? 'Sending OTP...' : 'Signing in...'}
                      </>
                    ) : (
                      loginMethod === 'otp' ? 'Send OTP' : 'Login'
                    )}
                  </button>
                </form>
              </div>
            )}

            {step === 2 && (
              <div className="step-transition">
                <form onSubmit={handleVerifyOTP} className="login-form">
                  {message && (
                    <div className="success-message">{message}</div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="otp" className="form-label">Enter OTP</label>
                    <input
                      id="otp"
                      type="text"
                      maxLength="6"
                      value={formData.otp}
                      onChange={(e) => handleInputChange('otp', e.target.value.replace(/\D/g, ''))}
                      className={`form-input text-center text-lg font-mono ${errors.otp ? 'border-red-300' : ''}`}
                      placeholder="000000"
                    />
                    {errors.otp && (
                      <p className="error-message">{errors.otp}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Check your email for the 6-digit OTP code
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 login-button"
                    >
                      {loading ? (
                        <>
                          <SpinnerSVG />
                          Verifying...
                        </>
                      ) : (
                        'Verify OTP'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="auth-quote">
              <p style={{ fontStyle: 'italic' }}>
                “{randomQuote.text}”
              </p>
              <p style={{ marginTop: '0.25rem', opacity: 0.8, fontSize: '0.9rem' }}>— {randomQuote.author}</p>
            </div>
          </div>
        </div>
      </div>

      {showPasswordSetupModal && (
        <div className="password-setup-overlay" onClick={closePasswordSetupModal}>
          <div className="password-setup-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="password-setup-header">
              <div>
                <h3>Set up your password</h3>
                <p>Create a password to use email login for your account.</p>
              </div>
              <button
                type="button"
                className="password-setup-close"
                onClick={closePasswordSetupModal}
                aria-label="Close password setup"
              >
                ×
              </button>
            </div>
            <form
              className="password-setup-body"
              onSubmit={passwordSetupStep === 1 ? handlePasswordSetupSendOtp : handlePasswordSetupSubmit}
            >
              {passwordSetupMessage && (
                <div className="success-message">{passwordSetupMessage}</div>
              )}

              {passwordSetupStep !== 3 && (
                <>
                  <div className="form-group">
                    <label htmlFor="password-setup-email" className="form-label">Email</label>
                    <input
                      id="password-setup-email"
                      type="email"
                      value={passwordSetupData.email}
                      onChange={(e) => handlePasswordSetupInputChange('email', e.target.value)}
                      className={`form-input ${passwordSetupErrors.email ? 'border-red-300' : ''}`}
                      placeholder="Enter your email address"
                    />
                    {passwordSetupErrors.email && (
                      <p className="error-message">{passwordSetupErrors.email}</p>
                    )}
                  </div>

                  {passwordSetupStep >= 2 && (
                    <>
                      <div className="form-group">
                        <label htmlFor="password-setup-otp" className="form-label">OTP</label>
                        <input
                          id="password-setup-otp"
                          type="text"
                          maxLength="6"
                          value={passwordSetupData.otp}
                          onChange={(e) => handlePasswordSetupInputChange('otp', e.target.value.replace(/\D/g, ''))}
                          className={`form-input text-center text-lg font-mono ${passwordSetupErrors.otp ? 'border-red-300' : ''}`}
                          placeholder="000000"
                        />
                        {passwordSetupErrors.otp && (
                          <p className="error-message">{passwordSetupErrors.otp}</p>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="password-setup-password" className="form-label">New Password</label>
                        <div className="password-input-container">
                          <input
                            id="password-setup-password"
                            type={showSetupPassword ? 'text' : 'password'}
                            value={passwordSetupData.password}
                            onChange={(e) => handlePasswordSetupInputChange('password', e.target.value)}
                            className={`form-input ${passwordSetupErrors.password ? 'border-red-300' : ''}`}
                            placeholder="Enter a new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSetupPassword(!showSetupPassword)}
                            className="password-toggle"
                          >
                            {showSetupPassword ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {passwordSetupErrors.password && (
                          <p className="error-message">{passwordSetupErrors.password}</p>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="password-setup-confirm" className="form-label">Confirm Password</label>
                        <div className="password-input-container">
                          <input
                            id="password-setup-confirm"
                            type={showSetupConfirmPassword ? 'text' : 'password'}
                            value={passwordSetupData.confirmPassword}
                            onChange={(e) => handlePasswordSetupInputChange('confirmPassword', e.target.value)}
                            className={`form-input ${passwordSetupErrors.confirmPassword ? 'border-red-300' : ''}`}
                            placeholder="Confirm your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSetupConfirmPassword(!showSetupConfirmPassword)}
                            className="password-toggle"
                          >
                            {showSetupConfirmPassword ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {passwordSetupErrors.confirmPassword && (
                          <p className="error-message">{passwordSetupErrors.confirmPassword}</p>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="password-setup-actions">
                {passwordSetupStep === 1 && (
                  <>
                    <button
                      type="button"
                      className="password-setup-button secondary"
                      onClick={closePasswordSetupModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="password-setup-button primary"
                      disabled={passwordSetupOtpLoading}
                    >
                      {passwordSetupOtpLoading ? 'Sending OTP...' : 'Send OTP'}
                    </button>
                  </>
                )}

                {passwordSetupStep === 2 && (
                  <>
                    <button
                      type="button"
                      className="password-setup-button secondary"
                      onClick={handlePasswordSetupSendOtp}
                      disabled={passwordSetupOtpLoading}
                    >
                      {passwordSetupOtpLoading ? 'Resending...' : 'Resend OTP'}
                    </button>
                    <button
                      type="submit"
                      className="password-setup-button primary"
                      disabled={passwordSetupLoading}
                    >
                      {passwordSetupLoading ? 'Setting...' : 'Set Password'}
                    </button>
                  </>
                )}

                {passwordSetupStep === 3 && (
                  <button
                    type="button"
                    className="password-setup-button primary"
                    onClick={closePasswordSetupModal}
                  >
                    Back to Login
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Panel Selection Modal - Never show for super admin or when user is null (after logout) */}
      {/* Only render if all conditions are met: user exists, not loading, panel selection is true, and localStorage has auth data */}
      {(() => {
        const hasToken = !!localStorage.getItem('token');
        const hasUserData = !!localStorage.getItem('user');
        const shouldShow = user && 
                          !authLoading && 
                          showPanelSelection && 
                          hasToken && 
                          hasUserData &&
                          !(user?.isSuperAdmin && user?.email === 'admin@mastersunion.org');
        
        if (!shouldShow) return null;
        
        return (
          <PanelSelectionModal
            isOpen={true}
            onClose={() => setShowPanelSelection(false)}
            user={user}
            showSuperAdmin={user?.isManager}
          />
        );
      })()}
    </div>
  );
}

export default Login;
